
import { useEffect, useRef } from "react";
import { CellPosition } from "../types/workout-types";

export function useCellTransition(
  activeCell: CellPosition | null,
  editMode: boolean,
  inputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>
) {
  // Track the previously active cell to prevent flickering
  const prevActiveCellRef = useRef<CellPosition | null>(null);
  
  // Handle cell state transitions with debouncing
  const cellTransitionTimeoutRef = useRef<number | null>(null);
  const DEBOUNCE_TIME = 150; // Delay for focus to prevent rapid flickering
  
  useEffect(() => {
    // Handle active cell changes with debouncing
    if (JSON.stringify(prevActiveCellRef.current) !== JSON.stringify(activeCell)) {
      // Clear any existing transition timeout
      if (cellTransitionTimeoutRef.current !== null) {
        window.clearTimeout(cellTransitionTimeoutRef.current);
      }
      
      cellTransitionTimeoutRef.current = window.setTimeout(() => {
        prevActiveCellRef.current = activeCell;
        
        if (activeCell && editMode) {
          const cellId = `cell-${activeCell.rowIndex}-${activeCell.colIndex}`;
          const input = inputRefs.current[cellId];
          
          if (input) {
            input.focus();
            const length = input.value.length;
            try {
              input.setSelectionRange(length, length);
            } catch (e) {
              console.log("Could not set selection range");
            }
          }
        }
        
        cellTransitionTimeoutRef.current = null;
      }, DEBOUNCE_TIME);
    }
    
    return () => {
      if (cellTransitionTimeoutRef.current !== null) {
        window.clearTimeout(cellTransitionTimeoutRef.current);
      }
    };
  }, [activeCell, editMode, inputRefs]);

  return { prevActiveCell: prevActiveCellRef.current };
}
